import { useEffect, useRef, useState, type CSSProperties } from 'react';
import {
  EditorContent,
  useEditor,
  useEditorState,
  type Editor,
} from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { VariableNode } from './variable-node';
import { VariableMenu } from './variable-menu.component';

interface RichTextEditorProps {
  html: string;
  onChange: (html: string) => void;
  /** Inline-only mode: disables lists (e.g. for the info-box body). */
  simple?: boolean;
}

/**
 * TipTap rich-text editor for `text` and `info-box` blocks. Emits the block's
 * HTML on every change. `simple` mode keeps the markup inline-only (no lists).
 */
export function RichTextEditor({
  html,
  onChange,
  simple = false,
}: RichTextEditorProps) {
  // Hold the latest onChange in a ref so the editor is created once per mount.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const editor = useEditor(
    {
      extensions: [
        StarterKit.configure({
          heading: false,
          code: false,
          codeBlock: false,
          blockquote: false,
          horizontalRule: false,
          ...(simple
            ? {
                bulletList: false,
                orderedList: false,
                listItem: false,
                listKeymap: false,
              }
            : {}),
          link: { openOnClick: false, autolink: true, defaultProtocol: 'https' },
        }),
        VariableNode,
      ],
      content: html && html.length > 0 ? html : '<p></p>',
      editorProps: {
        attributes: { class: 'eb-rte-content' },
      },
      onUpdate: ({ editor }) => onChangeRef.current(editor.getHTML()),
    },
    [],
  );

  if (!editor) return null;

  return (
    <div className="eb-rte">
      <RteToolbar editor={editor} simple={simple} />
      <EditorContent editor={editor} />
    </div>
  );
}

interface ToolbarButtonProps {
  active: boolean;
  label: string;
  title: string;
  onClick: () => void;
  glyphStyle?: CSSProperties;
}

function ToolbarButton({
  active,
  label,
  title,
  onClick,
  glyphStyle,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      title={title}
      aria-pressed={active}
      className={`eb-rte-btn${active ? ' active' : ''}`}
      onMouseDown={(e) => e.preventDefault()}
      onClick={onClick}
    >
      <span style={glyphStyle}>{label}</span>
    </button>
  );
}

function RteToolbar({ editor, simple }: { editor: Editor; simple: boolean }) {
  const [variableMenuOpen, setVariableMenuOpen] = useState(false);
  const variableButtonRef = useRef<HTMLButtonElement | null>(null);

  const state = useEditorState({
    editor,
    selector: ({ editor }) => ({
      bold: editor.isActive('bold'),
      italic: editor.isActive('italic'),
      underline: editor.isActive('underline'),
      bulletList: editor.isActive('bulletList'),
      orderedList: editor.isActive('orderedList'),
      link: editor.isActive('link'),
    }),
  });

  const insertVariable = (token: string) => {
    setVariableMenuOpen(false);
    editor.chain().focus().insertVariable(token).run();
  };

  const setLink = () => {
    const previous = (editor.getAttributes('link').href as string) ?? '';
    const url = window.prompt(
      'Link-URL (leer lassen, um den Link zu entfernen):',
      previous,
    );
    if (url === null) return;
    const chain = editor.chain().focus().extendMarkRange('link');
    if (url.trim() === '') {
      chain.unsetLink().run();
      return;
    }
    chain.setLink({ href: url.trim() }).run();
  };

  return (
    <div className="eb-rte-toolbar">
      <ToolbarButton
        active={state.bold}
        label="B"
        title="Fett"
        glyphStyle={{ fontWeight: 700 }}
        onClick={() => editor.chain().focus().toggleBold().run()}
      />
      <ToolbarButton
        active={state.italic}
        label="I"
        title="Kursiv"
        glyphStyle={{ fontStyle: 'italic' }}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      />
      <ToolbarButton
        active={state.underline}
        label="U"
        title="Unterstrichen"
        glyphStyle={{ textDecoration: 'underline' }}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      />
      {!simple && (
        <>
          <span className="eb-rte-sep" />
          <ToolbarButton
            active={state.bulletList}
            label="• Liste"
            title="Aufzählung"
            onClick={() => editor.chain().focus().toggleBulletList().run()}
          />
          <ToolbarButton
            active={state.orderedList}
            label="1. Liste"
            title="Nummerierte Liste"
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
          />
        </>
      )}
      <span className="eb-rte-sep" />
      <ToolbarButton
        active={state.link}
        label="Link"
        title="Link einfügen / bearbeiten"
        onClick={setLink}
      />
      <span className="eb-rte-sep" />
      <span className="eb-rte-variable-wrap">
        <button
          ref={variableButtonRef}
          type="button"
          title="Variable einfügen ({{anrede}}, {{grussformel}}, …)"
          aria-pressed={variableMenuOpen}
          aria-haspopup="menu"
          className={`eb-rte-btn${variableMenuOpen ? ' active' : ''}`}
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => setVariableMenuOpen((open) => !open)}
        >
          <span>{'{{ }}'}</span>
        </button>
        {variableMenuOpen && (
          <VariableMenu
            onSelect={insertVariable}
            onClose={() => setVariableMenuOpen(false)}
            anchorRef={variableButtonRef}
          />
        )}
      </span>
    </div>
  );
}
